import type { Dayjs } from "dayjs";
import "dayjs/locale/sl";

export type CalendarView = "day" | "week" | "month";

export type CalendarDateRange = { dateFrom: string; dateTo: string };

/**
 * The span the calendar is showing, which is also the span it fetched: these are
 * the same two expressions `CalendarPage` passes to `api.timeoff.useGetTimeoff`.
 * Deriving it a second way would let the assistant's idea of "this week" drift
 * from the week on screen.
 *
 * The grid is Monday-first in every UI language, so the week is read in `sl`
 * rather than `selectedDate`'s own locale, which may be Sunday-first.
 */
export function calendarDateRange(
  selectedDate: Dayjs,
  view: CalendarView,
): CalendarDateRange {
  return {
    dateFrom: selectedDate.locale("sl").startOf(view).format("YYYY-MM-DD"),
    dateTo: selectedDate.locale("sl").endOf(view).format("YYYY-MM-DD"),
  };
}

/**
 * How that span reads in the chip. Display only — it never leaves the browser,
 * so unlike the payload it is free to be prose.
 */
export function calendarRangeLabel(
  selectedDate: Dayjs,
  view: CalendarView,
): string {
  if (view === "day") {
    return selectedDate.format("D. M. YYYY");
  }
  if (view === "month") {
    return selectedDate.format("MMMM YYYY");
  }

  const from = selectedDate.locale("sl").startOf("week");
  const to = selectedDate.locale("sl").endOf("week");
  // A week can straddle a year end, and then both ends need their year.
  return from.year() === to.year()
    ? `${from.format("D. M.")} – ${to.format("D. M. YYYY")}`
    : `${from.format("D. M. YYYY")} – ${to.format("D. M. YYYY")}`;
}
